import { profileUpdateSchema } from '@fruuit/shared'
import { Avatar, Badge, Button, Card, Input, PageHeader, useToast } from '@fruuit/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { errorMessage } from '../api/client'
import { useApiMutation } from '../api/hooks'
import { meQueryKey } from '../api/session'
import { roleSummary } from '../areas/areas'
import { updateProfile } from '../features/auth/api'
import { ROLE_LABELS } from '../features/auth/format'
import { MfaSettings } from '../features/auth/MfaSettings'
import { SessionsList } from '../features/auth/SessionsList'
import { useTitle } from '../lib/useTitle'
import { useSession } from './session-context'

function ProfileCard() {
  const me = useSession()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(me.user.display_name)
  const [fieldError, setFieldError] = useState<string | undefined>()
  const save = useApiMutation(updateProfile, {
    onSuccess: async () => {
      setEditing(false)
      await queryClient.invalidateQueries({ queryKey: meQueryKey })
      toast({ tone: 'success', message: 'Your name was updated.' })
    },
  })
  const display = me.user.display_name || me.user.email
  const impersonated = me.impersonated_by !== null

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const parsed = profileUpdateSchema.safeParse({ revision: me.user.revision, display_name: name })
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message)
      return
    }
    setFieldError(undefined)
    save.mutate(parsed.data)
  }

  return (
    <Card
      title="Profile"
      actions={
        !editing && !impersonated ? (
          <Button variant="ghost" size="sm" icon={<Pencil />} onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : null
      }
    >
      <div className="flex items-center gap-4">
        <Avatar name={display} size="lg" />
        <div className="min-w-0">
          <p className="truncate font-semibold text-ink">{display}</p>
          <p className="truncate text-sm text-sage">{me.user.email}</p>
        </div>
      </div>
      {editing ? (
        <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-3 border-t border-line pt-5">
          <Input
            label="Display name"
            name="display_name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={fieldError ?? (save.error ? errorMessage(save.error) : undefined)}
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={save.isPending}>
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false)
                setName(me.user.display_name)
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      <dl className="mt-6 grid gap-4 border-t border-line pt-5 text-sm">
        <div>
          <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">Roles</dt>
          <dd className="mt-1 text-ink">{roleSummary(me) || 'No roles assigned'}</dd>
        </div>
      </dl>
    </Card>
  )
}

/** A-10 account page: profile, organisations, two-factor authentication and sessions. */
export function AccountPage() {
  useTitle('Account')
  const me = useSession()

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Account & security"
        description="Your profile, organisations and sign-in security."
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <ProfileCard />
        <Card title="Organisations">
          {me.memberships.length === 0 ? (
            <p className="text-sm text-sage">You are not a member of any organisation.</p>
          ) : (
            <ul className="divide-y divide-line">
              {me.memberships.map((membership) => (
                <li
                  key={`${membership.org_id}-${membership.role}`}
                  className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <span className="min-w-0 truncate text-sm font-medium text-ink">
                    {membership.org_name ?? membership.org_id}
                  </span>
                  <Badge tone="outline">{ROLE_LABELS[membership.role] ?? membership.role}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card id="security" title="Two-factor authentication" className="lg:col-span-2">
          <MfaSettings />
        </Card>
        <Card title="Where you're signed in" className="lg:col-span-2">
          <SessionsList />
        </Card>
      </div>
    </>
  )
}
